import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, downloadFile } from "@/lib/api";
import { PageHeader, Loading, StatusPill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, RefreshCcw, Save, Loader2, CheckCircle2, ShieldCheck, Building2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { YearSummary } from "@/components/billing/YearSummary";

function InvoiceButton({ payment }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await downloadFile(`/billing/payments/${payment.payment_id}/invoice`, `vuzurent-invoice-${payment.period}.pdf`); toast.success("Invoice downloaded"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-primary hover:bg-emerald-50 px-2" onClick={dl} disabled={busy} data-testid={`billing-invoice-${payment.period}`}>
      <FileDown className="h-3.5 w-3.5" /> {busy ? "…" : "Invoice"}
    </Button>
  );
}

function LockedPlanCard({ ov }) {
  const a = ov.access || {};
  const until = a.until ? new Date(a.until + "T12:00:00Z").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-plan-locked">
      <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Your plan</h2>
      <p className="text-sm text-slate-500 mb-4">Set by VuZuRent for your account. Contact your administrator to change it.</p>
      <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
        <span className="text-sm text-slate-600">Monthly fee</span>
        <span className="font-display font-bold text-lg text-slate-900" data-testid="billing-plan-locked-amount">{fmtMoney(ov.amount_due)}<span className="text-xs text-slate-400 font-normal">/mo</span></span>
      </div>
      {until && (
        <div className="mt-3 flex items-center justify-between text-sm" data-testid="billing-access-until">
          <span className="text-slate-600">{a.in_trial ? "Free trial ends" : a.state === "expired" ? "Access ended" : "Access runs through"}</span>
          <span className={`font-semibold ${a.state === "expired" ? "text-rose-600" : a.state === "ending_soon" ? "text-amber-700" : "text-slate-900"}`}>{until}{a.days_left >= 0 ? ` · ${a.days_left} day${a.days_left === 1 ? "" : "s"} left` : ""}</span>
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-3">Each month you pay extends your access to the end of that month. Turn on auto-pay so it never lapses.</p>
    </div>
  );
}

function PlanCard({ ov, onSaved }) {
  const s = ov.settings;
  const [form, setForm] = useState({ model: s.model, flat_amount: s.flat_amount, per_property_amount: s.per_property_amount });
  const [fees, setFees] = useState(() => Object.fromEntries(ov.per_house.map((h) => [h.property_id, h.custom ? h.fee : ""])));
  const [saving, setSaving] = useState(false);

  const houseFee = (h) => {
    const v = fees[h.property_id];
    return v === "" || v === undefined || v === null ? Number(form.per_property_amount || 0) : Number(v) || 0;
  };
  const preview = form.model === "per_property" ? ov.per_house.reduce((sum, h) => sum + houseFee(h), 0) : Number(form.flat_amount || 0);

  const save = async () => {
    setSaving(true);
    try {
      const property_fees = Object.fromEntries(Object.entries(fees).filter(([, v]) => v !== "" && v !== null && v !== undefined).map(([k, v]) => [k, Number(v) || 0]));
      await api.put("/billing/settings", { model: form.model, flat_amount: Number(form.flat_amount) || 0, per_property_amount: Number(form.per_property_amount) || 0, property_fees });
      toast.success("Plan updated");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-plan">
      <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Your plan</h2>
      <p className="text-sm text-slate-500 mb-5">Choose how the monthly property-management fee is calculated.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Pricing model</Label>
          <Select value={form.model} onValueChange={(v) => setForm((f) => ({ ...f, model: v }))}>
            <SelectTrigger data-testid="billing-model"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="flat">Flat fee per month</SelectItem><SelectItem value="per_property">Per property per month</SelectItem></SelectContent>
          </Select></div>
        {form.model === "flat" ? (
          <div className="space-y-1.5"><Label className="text-xs">Monthly fee ($)</Label>
            <Input type="number" min={0} value={form.flat_amount} onChange={(e) => setForm((f) => ({ ...f, flat_amount: e.target.value }))} data-testid="billing-flat" /></div>
        ) : (
          <div className="space-y-1.5"><Label className="text-xs">Default per property ($) · {ov.property_count} houses</Label>
            <Input type="number" min={0} value={form.per_property_amount} onChange={(e) => setForm((f) => ({ ...f, per_property_amount: e.target.value }))} data-testid="billing-per-property" /></div>
        )}
      </div>

      {form.model === "per_property" && (
        <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden" data-testid="billing-house-fees">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fee per house</span>
            <button type="button" onClick={() => setFees(Object.fromEntries(ov.per_house.map((h) => [h.property_id, ""])))} className="text-[11px] font-semibold text-primary hover:underline" data-testid="billing-house-fees-reset">Use default for all</button>
          </div>
          <div className="divide-y divide-slate-100">
            {ov.per_house.map((h) => (
              <div key={h.property_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`billing-house-${h.property_id}`}>
                <div className="min-w-0 flex-1"><div className="text-sm font-medium text-slate-800 truncate">{h.name}</div><div className="text-[11px] text-slate-400 truncate">{h.address}</div></div>
                <div className="relative w-28">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                  <Input type="number" min={0} value={fees[h.property_id] ?? ""} placeholder={String(form.per_property_amount)} data-testid={`billing-house-fee-${h.property_id}`}
                    onChange={(e) => setFees((f) => ({ ...f, [h.property_id]: e.target.value }))} className="h-9 pl-6 text-right font-mono text-sm" />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 px-4 py-2 border-t border-slate-100">Leave a house blank to use the default amount. Each house is calculated separately; the total below is what you pay.</p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
        <span className="text-sm text-slate-600">Total monthly fee</span>
        <span className="font-display font-bold text-lg text-slate-900" data-testid="billing-plan-preview">{fmtMoney(preview)}<span className="text-xs text-slate-400 font-normal">/mo</span></span>
      </div>
      {s.autopay && <p className="text-[11px] text-amber-700 mt-2">Auto-pay is active at {fmtMoney(s.subscription_amount)}/mo. Changing the plan updates one-time payments right away; to change the auto-pay amount, cancel and turn it on again.</p>}
      <Button onClick={save} disabled={saving} className="w-full gap-2 mt-4" data-testid="billing-plan-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save plan"}</Button>
    </div>
  );
}

function HouseBreakdown({ ov }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="billing-breakdown">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-lg text-slate-900">Fee by house · {ov.current_label}</h3>
        <span className="text-xs text-slate-500">{ov.plan_desc}</span>
      </div>
      <div className="divide-y divide-slate-100">
        {ov.per_house.map((h) => (
          <div key={h.property_id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`billing-breakdown-${h.property_id}`}>
            <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{h.name}</div><div className="text-[11px] text-slate-400 truncate">{h.address}</div></div>
            <span className="font-mono text-sm font-semibold text-slate-900 shrink-0">
              {h.fee === null ? <span className="text-xs font-sans font-normal text-slate-400">included in flat fee</span> : fmtMoney(h.fee)}
              {h.custom && <span className="ml-2 rounded-full bg-indigo-50 text-indigo-700 text-[9px] font-bold px-1.5 py-0.5 align-middle font-sans">CUSTOM</span>}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between pt-3">
          <span className="text-sm font-semibold text-slate-700">Total monthly fee</span>
          <span className="font-display font-bold text-slate-900" data-testid="billing-breakdown-total">{fmtMoney(ov.amount_due)}</span>
        </div>
      </div>
    </div>
  );
}

export default function Billing() {
  const [ov, setOv] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/billing/overview").then((r) => setOv(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const payNow = async () => {
    setBusy("pay");
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };
  const subscribe = async () => {
    setBusy("sub");
    try {
      const { data } = await api.post("/billing/subscribe", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };
  const cancelAutopay = async () => {
    setBusy("cancel");
    try { await api.post("/billing/autopay/cancel"); toast.success("Auto-pay turned off"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  if (!ov) return <Loading />;
  const s = ov.settings;

  return (
    <div>
      <PageHeader title="Billing" subtitle="Your monthly VuZuRent property-management fee, paid securely by card." />

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 space-y-5">
          <div className="rounded-2xl bg-slate-900 text-white p-7 relative overflow-hidden" data-testid="billing-hero">
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-primary/30 blur-3xl" />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-wider text-white/60">Management fee · {ov.current_label}</div>
              <div className="flex items-end gap-3 mt-2">
                <div className="font-display font-extrabold text-4xl sm:text-5xl" data-testid="billing-amount">{fmtMoney(ov.amount_due)}</div>
                <span className="mb-2"><StatusPill status={ov.current_paid ? "paid" : "due"} /></span>
              </div>
              <div className="text-sm text-white/70 mt-1 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {ov.plan_desc} · {ov.property_count} propert{ov.property_count === 1 ? "y" : "ies"}</div>
              <div className="flex flex-wrap gap-2 mt-6">
                {ov.current_paid ? (
                  <div className="inline-flex items-center gap-2 rounded-xl bg-emerald-500/20 text-emerald-200 px-4 py-2.5 text-sm font-semibold" data-testid="billing-paid-badge"><CheckCircle2 className="h-4 w-4" /> {ov.current_label} is paid</div>
                ) : ov.current_trial ? (
                  <div className="inline-flex items-center gap-2 rounded-xl bg-indigo-500/25 text-indigo-100 px-4 py-2.5 text-sm font-semibold" data-testid="billing-trial-badge"><CheckCircle2 className="h-4 w-4" /> {ov.current_label} is covered by your free trial</div>
                ) : (
                  <Button onClick={payNow} disabled={!!busy || ov.amount_due <= 0} className="gap-2 bg-white text-slate-900 hover:bg-slate-100 h-11" data-testid="billing-pay-now">
                    {busy === "pay" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay {fmtMoney(ov.amount_due)} for {ov.current_label}
                  </Button>
                )}
                {s.autopay ? (
                  <Button variant="outline" onClick={cancelAutopay} disabled={!!busy} className="gap-2 h-11 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="billing-cancel-autopay">
                    {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} Cancel auto-pay
                  </Button>
                ) : (
                  <Button variant="outline" onClick={subscribe} disabled={!!busy || ov.amount_due <= 0} className="gap-2 h-11 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="billing-autopay">
                    {busy === "sub" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} Turn on auto-pay
                  </Button>
                )}
              </div>
              <div className="mt-4 text-xs text-white/60 flex items-center gap-1.5" data-testid="billing-autopay-status">
                <ShieldCheck className="h-3.5 w-3.5" />
                {s.autopay ? `Auto-pay is on — ${fmtMoney(s.subscription_amount)} is charged to your card automatically each month.` : "Auto-pay is off — pay each month manually, or turn on auto-pay to never miss a month."}
                {ov.stripe_mode === "test" && <span className="ml-1 rounded bg-amber-400/20 text-amber-200 px-1.5 py-0.5 text-[10px] font-semibold">TEST MODE · card 4242 4242 4242 4242</span>}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Payment history</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr><th className="text-left font-semibold py-2.5">Month</th><th className="text-right font-semibold py-2.5">Amount</th><th className="text-left font-semibold py-2.5 pl-4">Paid</th><th className="text-right font-semibold py-2.5">Status</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {ov.history.map((h) => (
                    <tr key={h.period} data-testid={`billing-row-${h.period}`}>
                      <td className="py-3 font-medium text-slate-800">{h.label}</td>
                      <td className="py-3 text-right font-mono text-xs font-semibold">{fmtMoney(h.amount)}</td>
                      <td className="py-3 pl-4 text-xs text-slate-500">{h.paid_at ? `${new Date(h.paid_at).toLocaleDateString()} · ${h.source === "subscription" ? "auto-pay" : "card"}` : "—"}</td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <StatusPill status={h.status} />
                          {h.payment_id && <InvoiceButton payment={h} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-5">
          {ov.plan_locked ? <LockedPlanCard ov={ov} /> : <PlanCard key={`${s.model}-${s.flat_amount}-${s.per_property_amount}-${JSON.stringify(s.property_fees || {})}`} ov={ov} onSaved={load} />}
          {!ov.plan_locked && <HouseBreakdown ov={ov} />}
          <YearSummary />
        </div>
      </div>
    </div>
  );
}
